import { LayoutGrid, ShieldAlert, WifiOff } from 'lucide-react';
import { useEffect, useState } from 'react';
import { Button } from '@/components/Button';
import { CampaignsMark } from '@/components/HazcheckMark';
import { button } from '@/components/button-variants';
import { signIn, type Session } from '@/auth/keycloak';
import { getConfig } from '@/lib/config';

/**
 * The route guard.
 *
 * It is a **UX affordance only** — the API authorises every request server-side with a
 * deny-by-default filter. This exists so an unauthenticated visitor sees a sign-in page instead of
 * a screen full of failed requests, not to protect anything. Anyone editing this should assume it
 * can be bypassed and check that nothing depends on it not being.
 */
export function AuthBoundary({
  session,
  children,
}: {
  session: Session;
  children: React.ReactNode;
}) {
  if (!session.isAuthenticated) return <SignInPrompt />;

  // Authenticated but with no Campaigns role at all. Distinct from unauthenticated, and a sign-in
  // button here would be a loop — they are already signed in; the realm has not granted them a
  // role on this client.
  if (session.roles.length === 0) return <NoRolePrompt session={session} />;

  return <>{children}</>;
}

/** Whether the identity provider is actually reachable. */
type ProviderState = 'checking' | 'reachable' | 'unreachable';

function SignInPrompt() {
  // Preserves where they were headed, so a bookmarked campaign URL survives the sign-in round trip.
  const returnTo = `${window.location.pathname}${window.location.search}`;

  const [provider, setProvider] = useState<ProviderState>('checking');

  // Synchronising with something outside React — whether another service is up.
  //
  // Worth the request because Keycloak is the single most likely thing to be down in local
  // development: the realm is shared and runs from the HazcheckHub repository, not this one. Without
  // this check, clicking Sign in navigates to a dead host and the user gets the browser's own
  // "cannot connect" page, which says nothing about which service or why.
  useEffect(() => {
    let cancelled = false;

    const { auth } = getConfig();

    async function check() {
      try {
        const response = await fetch(
          `${auth.url}/realms/${auth.realm}/.well-known/openid-configuration`,
          { signal: AbortSignal.timeout(4000) },
        );

        if (!cancelled) setProvider(response.ok ? 'reachable' : 'unreachable');
      } catch {
        if (!cancelled) setProvider('unreachable');
      }
    }

    void check();

    return () => {
      cancelled = true;
    };
  }, []);

  return (
    <main className="flex min-h-dvh items-center justify-center bg-bg px-6">
      <div className="w-full max-w-sm rounded-lg border border-border bg-surface p-8 shadow-md">
        <div className="mb-6 flex items-center gap-3">
          <CampaignsMark className="size-9 shrink-0" />
          <h1 className="text-lg text-heading">
            Hazcheck <b className="font-bold">Campaigns</b>
          </h1>
        </div>

        <p className="mb-6 text-sm text-muted">
          Sign in with your Hazcheck account to author and send campaigns.
        </p>

        {/* The button stays enabled even when the provider looks unreachable. Disabling it would
            give no reason and no keyboard affordance, and the check can be wrong — a proxy that
            blocks our fetch but allows a top-level navigation is a real configuration. */}
        <Button
          variant="primary"
          className="w-full"
          onClick={() => void signIn(returnTo)}
          autoFocus
        >
          Sign in
        </Button>

        {provider === 'unreachable' && (
          <div
            className="mt-4 flex items-start gap-2 rounded-md bg-warning-bg px-3 py-2 text-xs text-warning-ink"
            role="status"
          >
            <WifiOff className="mt-0.5 h-3.5 w-3.5 shrink-0" aria-hidden="true" />
            <span>
              Hazcheck sign-in is unavailable. Try again shortly. If it stays unavailable, contact
              your administrator.
            </span>
          </div>
        )}
        <a
          href={getConfig().hubUrl}
          className={button({ variant: 'link', className: 'mt-3 w-full' })}
        >
          <LayoutGrid className="size-4" aria-hidden="true" />
          Back to HazcheckHub
        </a>
      </div>
    </main>
  );
}

function NoRolePrompt({ session }: { session: Session }) {
  return (
    <main className="flex min-h-dvh items-center justify-center bg-bg px-6">
      <div className="w-full max-w-md rounded-lg border border-border bg-surface p-8 shadow-md">
        <div className="mb-4 flex items-center gap-3">
          <div className="rounded-md bg-warning-bg p-2">
            <ShieldAlert className="h-5 w-5 text-warning-ink" aria-hidden="true" />
          </div>
          <h1 className="text-lg font-semibold text-heading">No access yet</h1>
        </div>

        <p className="text-sm text-muted">
          You are signed in as{' '}
          <strong className="font-medium text-foreground">{session.email ?? session.name}</strong>,
          but your account has no Campaigns role.
        </p>

        <p className="mt-3 text-sm text-muted">
          Ask your HazcheckHub administrator for Campaigns access. They can assign the role you need
          in Access management. Then sign in again to use your new access.
        </p>
        <a
          href={getConfig().hubUrl}
          className={button({ variant: 'secondary', className: 'mt-6 w-full' })}
        >
          <LayoutGrid className="size-4" aria-hidden="true" />
          Back to HazcheckHub
        </a>
      </div>
    </main>
  );
}
